import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Check, ChevronDown, Plus, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Combobox to pick a bank (bookmaker).
 * banks: [{ id, name, account_holder }]
 * value: string (bank name)
 * onChange: (name) => void
 */
export default function BankSelector({
  banks = [],
  value = "",
  onChange,
  placeholder = "Selecionar casa",
  testId,
  onBankCreated,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [creating, setCreating] = React.useState(false); // show holder step
  const [holder, setHolder] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const holders = React.useMemo(() => {
    const set = new Set();
    for (const b of banks) if (b.account_holder && b.account_holder.trim()) set.add(b.account_holder.trim());
    return [...set].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [banks]);

  const createBank = async () => {
    const name = query.trim();
    if (!name) return;
    setSaving(true);
    try {
      const res = await api.post("/banks", { name, initial_balance: 0, account_holder: holder.trim() || null });
      toast.success(`Banca "${name}" cadastrada`);
      if (onBankCreated) onBankCreated(res.data);
      setCreating(false);
      setHolder("");
      commit(name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cadastrar banca");
    } finally {
      setSaving(false);
    }
  };

  const commit = (name) => {
    onChange(name);
    setOpen(false);
    setQuery("");
  };

  // Group banks by holder
  const groups = React.useMemo(() => {
    const g = {};
    for (const b of banks) {
      const key = (b.account_holder && b.account_holder.trim()) || "Sem titular";
      if (!g[key]) g[key] = [];
      g[key].push(b);
    }
    return g;
  }, [banks]);
  const groupKeys = Object.keys(groups).sort((a, b) => {
    if (a === "Sem titular") return 1;
    if (b === "Sem titular") return -1;
    return a.localeCompare(b, "pt-BR");
  });

  const showFreeText =
    query.trim() &&
    !banks.some((b) => b.name.toLowerCase() === query.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setCreating(false); setHolder(""); } }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          data-testid={testId}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full justify-between border-white/10 bg-black/40 px-3 font-normal text-white hover:bg-black/60 hover:text-white"
          style={{ transition: "background-color 0.2s ease" }}
        >
          <span className={cn("truncate", !value && "text-zinc-500")}>
            {value || placeholder}
          </span>
          <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] border-white/10 bg-[#121212] p-0"
        align="start"
      >
        <Command shouldFilter={false} className="bg-transparent">
          <CommandInput
            placeholder="Buscar ou digitar nova casa..."
            value={query}
            onValueChange={setQuery}
            className="border-b border-white/10 text-white"
          />
          <CommandList className="max-h-72">
            {banks.length === 0 && (
              <CommandEmpty className="py-4 text-center text-xs text-zinc-500">
                Nenhuma banca cadastrada — digite o nome livremente.
              </CommandEmpty>
            )}
            {groupKeys.map((holder) => {
              const filtered = groups[holder].filter((b) =>
                b.name.toLowerCase().includes(query.toLowerCase())
              );
              if (filtered.length === 0) return null;
              return (
                <CommandGroup
                  key={holder}
                  heading={
                    <span className="text-[0.7rem] uppercase tracking-widest text-[#D4FF00]">
                      {holder}
                    </span>
                  }
                >
                  {filtered.map((b) => (
                    <CommandItem
                      key={b.id}
                      value={b.name}
                      onSelect={() => commit(b.name)}
                      className="flex cursor-pointer items-center justify-between gap-2 text-sm text-zinc-200 aria-selected:bg-white/5 aria-selected:text-white"
                    >
                      <span className="flex items-center gap-2">
                        <Check
                          className={cn(
                            "h-3.5 w-3.5",
                            value === b.name ? "opacity-100 text-[#D4FF00]" : "opacity-0"
                          )}
                        />
                        <span className="font-medium">{b.name}</span>
                      </span>
                      <span className="text-[0.7rem] text-zinc-500">
                        {b.account_holder || "—"}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
            {showFreeText && !creating && (
              <CommandGroup
                heading={
                  <span className="text-[0.7rem] uppercase tracking-widest text-zinc-500">
                    Casa não cadastrada
                  </span>
                }
              >
                <CommandItem
                  value={`__create__${query}`}
                  data-testid={`${testId}-create-bank`}
                  onSelect={() => setCreating(true)}
                  className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#D4FF00] aria-selected:bg-[#D4FF00]/10"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Cadastrar &quot;{query.trim()}&quot; como nova banca
                </CommandItem>
                <CommandItem
                  value={query}
                  data-testid={`${testId}-use-free`}
                  onSelect={() => commit(query.trim())}
                  className="cursor-pointer text-sm text-zinc-300 aria-selected:bg-white/5"
                >
                  Usar &quot;{query.trim()}&quot; só nesta operação
                </CommandItem>
              </CommandGroup>
            )}
            {showFreeText && creating && (
              <div data-testid={`${testId}-create-form`} className="space-y-2 p-3">
                <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">
                  Nova banca: <span className="font-bold text-white">{query.trim()}</span>
                </div>
                <div className="text-[0.7rem] text-zinc-400">Titular da conta</div>
                {holders.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {holders.map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setHolder(h)}
                        className={cn(
                          "rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold",
                          holder === h ? "border-[#D4FF00] bg-[#D4FF00] text-black" : "border-white/10 text-zinc-300 hover:border-white/30"
                        )}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                )}
                <Input
                  data-testid={`${testId}-holder-input`}
                  value={holder}
                  onChange={(e) => setHolder(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createBank(); } }}
                  placeholder="Nome do titular (opcional)"
                  className="h-8 bg-black/40 text-sm"
                />
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCreating(false)} className="h-8 text-xs text-zinc-400 hover:text-white">
                    Voltar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`${testId}-create-save`}
                    onClick={createBank}
                    disabled={saving}
                    className="h-8 bg-[#D4FF00] text-xs font-bold text-black hover:bg-[#c4ee00]"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salvar banca"}
                  </Button>
                </div>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
